import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PREVENTIVE_TASK_PATH } from '../../../../config/paths';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import Content from '../../../layout/Content';

import { postPreventiveTask } from '../../../../actions/task-schedule/PreventiveTask';
import PreventiveTaskForm from './PreventiveTaskForm';

import {
  PREVENTIVE_TASK_MONTHLY_TYPE,
  PREVENTIVE_TASK_REPEAT_TYPE,
  PREVENTIVE_TASK_WEEK_ORDER,
} from '../../../../config/constants';

const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const isLastDayChecked = (value) =>
  value === true || value === 1 || value === '1';

const buildRepeatInterval = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (
    repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.value ||
    repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.value
  ) {
    return 1;
  }

  return Number(formValues.repeatInterval || 1);
};

const buildWeekdays = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.value) {
    return [];
  }

  if (!Array.isArray(formValues.weekdays)) {
    return [];
  }

  return formValues.weekdays
    .filter((item) => item && item.weekday)
    .map((item) => ({
      weekday: Number(item.weekday),
    }));
};

const buildMonthlyDays = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.value) {
    return [];
  }

  if (!Array.isArray(formValues.monthlyDays)) {
    return [];
  }

  return formValues.monthlyDays
    .filter((item) => item)
    .map((item) => {
      const lastDay = isLastDayChecked(item.isLastDayOfMonth);

      return {
        dayOfMonth: lastDay ? null : toNumberOrNull(item.dayOfMonth),
        isLastDayOfMonth: lastDay,
      };
    });
};

const buildMonthlyRules = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.value) {
    return [];
  }

  if (!Array.isArray(formValues.monthlyRules)) {
    return [];
  }

  return formValues.monthlyRules
    .filter((item) => item && item.monthlyType)
    .map((item) => {
      const monthlyType = getSelectValue(item.monthlyType);

      return {
        monthlyType,
        weekOrder:
          monthlyType ===
          PREVENTIVE_TASK_MONTHLY_TYPE.LAST_WEEKDAY_OF_MONTH.value
            ? PREVENTIVE_TASK_WEEK_ORDER.LAST.value
            : getSelectValue(item.weekOrder),
        weekday: getSelectValue(item.weekday),
      };
    });
};

const buildChecklistItems = (formValues) => {
  if (!Array.isArray(formValues.checklistItems)) {
    return [];
  }

  return formValues.checklistItems
    .filter((item) => item && item.name)
    .map((item, index) => ({
      name: item.name,
      description: item.description || null,
      sortOrder: index + 1,
    }));
};

const buildPreventiveTaskBody = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  return {
    preventiveTask: {
      name: formValues.name,
      companyPreventiveTaskId: formValues.companyPreventiveTaskId,
      description: formValues.description || null,
      actionType: getSelectValue(formValues.actionType),
      active: getSelectValue(formValues.active),
      priority: getSelectValue(formValues.priority),
      deviation: toNumberOrNull(formValues.deviation),
      repeatType,
      repeatInterval: buildRepeatInterval(formValues),
    },

    weekdays: buildWeekdays(formValues),
    monthlyDays: buildMonthlyDays(formValues),
    monthlyRules: buildMonthlyRules(formValues),
    checklistItems: buildChecklistItems(formValues),
  };
};

const NewPreventiveTask = ({
  breadcrumbs,
  dispatchPostPreventiveTask,
  loading,
}) => {
  const onSubmit = (formValues) => {
    const body = buildPreventiveTaskBody(formValues);

    dispatchPostPreventiveTask(body);
  };

  const content = (
    <PreventiveTaskForm
      onSubmit={onSubmit}
      loading={loading}
      initialValues={{
        active: true,
      }}
    />
  );
  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Nueva Tarea Programada"
      subtitle="Crear nueva tarea programada"
      content={content}
    />
  );
};

NewPreventiveTask.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchPostPreventiveTask: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

const mapDispatchToProps = {
  dispatchPostPreventiveTask: postPreventiveTask,
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  breadcrumbs: [
    ...TaskScheduleUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Lista de Tareas Programadas',
      href: PREVENTIVE_TASK_PATH,
    },
    {
      text: 'Nuevo',
      href: '',
    },
  ],
  loading: !TaskScheduleUnit.PreventiveTask.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewPreventiveTask);
